import { useState } from 'react';

import { gql } from '@apollo/client';
import { skipToken } from '@apollo/client/react';
import { getRailsEnv } from '@opendoor/shared-fe/railsEnv';
import {
  OdProtosPartnershipData_PartnershipType as PartnershipType,
  OdProtosPartnershipData_ReferralInvitationAcceptanceStatus as ReferralAcceptanceStatus,
  OdProtosPartnershipData_Referral_AttributionType as ReferralAttributionType,
} from '__generated__/globalTypes';
import type {
  PartnershipReferralsQuery_partnership_OdProtosPartnershipServices_Partnership_Query_getReferralsForSellerInput_OdProtosPartnershipServices_GetReferralsForSellerInputResponse_referrals_OdProtosPartnershipData_Referral as Referral,
  PartnershipReferralsQuery_partnership_OdProtosPartnershipServices_Partnership_Query_getReferralInvitation_OdProtosPartnershipServices_GetReferralInvitationResponse_invitation_OdProtosPartnershipData_ReferralInvitation as ReferralInvitation,
} from 'growth/__generated__/usePartnershipReferrals';
import {
  PartnershipReferralsDocument,
  ReferralInvitationEligibilityDocument,
} from 'growth/__generated__/usePartnershipReferrals';
import { useQuery } from 'platform/apollo';
import { offerPollingTimeoutThreshold } from 'shared/helpers/globals';
import { useTimeout } from 'shared/hooks/useTimeout';

const odRailsEnv = getRailsEnv();

/* Used for enabling buyer content for Redfin leads (if not blocked due to other conditions) */
const TEST_PARTNER_ORE_ID = 'test_partner_ore';
const RF_STAGING_ID = '31eaaf9e-5c26-4d2b-b402-43aba0369298';
const RF_PROD_ID = 'ce048db9-17f4-49c8-9ebf-2df9a6158fbd';

const BUYER_CONTENT_ELIGIBLE_ORE_PARTNER_IDENTIFIERS =
  odRailsEnv !== 'production'
    ? [TEST_PARTNER_ORE_ID, RF_STAGING_ID]
    : [TEST_PARTNER_ORE_ID, RF_PROD_ID];

/** Agent refer/represent choice or agent referral form (unknown attribution on the agency referral). */
export type AgentReferralStep = {
  type: 'agent-referral';
  agentReferral: Referral;
  referralInvitiationPaymentEligibility: boolean;
  partnershipIdentifiers: string[];
};

/** Seller accepts or declines a pending referral invitation. */
export type CustomerReferralAcceptanceStep = {
  type: 'customer-referral-acceptance';
  referralInvitation: ReferralInvitation;
  referralInvitiationPaymentEligibility: boolean;
};

export type ReferralStep = AgentReferralStep | CustomerReferralAcceptanceStep;

/**
 * Extracts and determines if the referral step is required.
 *
 * Previous comment:
 *     Channel check unnecessary because we would have had updated the
 *     attribution type if the agent opted for representation. If an ops
 *     user manually flipped to lead from agent -> sell direct, the
 *     agent referral would be deleted.
 *     The referralInvitiationEligibility criterion takes into account
 *     the listed status of the lead. Listed leads are not eligible.
 */
function extractPartnershipReferralStep(
  agentReferral: Referral | undefined,
  referralInvitiationEligibility: boolean,
  referralInvitation: ReferralInvitation | null,
  referralInvitiationPaymentEligibility: boolean,
  partnershipIdentifiers: string[],
): ReferralStep | undefined {
  // This represents that we need the agent to chose whether to refer or represent the customer
  const hasUnknownAttribution =
    agentReferral?.attributionType === ReferralAttributionType.UNKNOWN_ATTRIBUTION &&
    referralInvitiationEligibility;

  // REFERRAL means the agent referred; reverting sell-direct → agent flips attribution to REPRESENTATION.
  const isPendingAcceptance =
    agentReferral?.attributionType === ReferralAttributionType.REFERRAL &&
    referralInvitation?.status === ReferralAcceptanceStatus.PENDING;

  if (isPendingAcceptance && referralInvitation) {
    return {
      type: 'customer-referral-acceptance',
      referralInvitation,
      referralInvitiationPaymentEligibility,
    };
  }
  if (hasUnknownAttribution && agentReferral) {
    return {
      type: 'agent-referral',
      agentReferral,
      referralInvitiationPaymentEligibility,
      partnershipIdentifiers,
    };
  }

  return undefined;
}

export interface PartnershipReferralResponse {
  loading: boolean;
  isPartnershipLead: boolean;
  hasHomebuilderReferral: boolean;
  hasOreReferral: boolean;
  hasAgentReferral: boolean;
  partnershipIdentifiers?: Array<string>;
  isTrueAgentReferral: boolean;
  hasMarkSpainReferral: boolean;
  hasEasyStreetOffersReferral: boolean;
  isLennarTradeIn: boolean;
  referralStep: ReferralStep | undefined;
  blockBuyerContent: boolean;
}

gql`
  query PartnershipReferrals($sellerInputUUID: String!) {
    partnership {
      getReferralsForSellerInput(input: { sellerInputId: $sellerInputUUID }) {
        referrals {
          sellerRelation
          partnershipType
          partnerIdentifier
          attributionType
          uuid
          attributes {
            eligibleForAgentReferralPayment
          }
        }
      }
      getReferralInvitation(input: { sellerInputId: $sellerInputUUID }) {
        invitation {
          id
          status
          inviterName
          inviterEmail
        }
      }
    }
  }
`;

gql`
  query ReferralInvitationEligibility($sellerInputUUID: String!) {
    partnership {
      getReferralInvitationEligibility(input: { sellerInputId: $sellerInputUUID }) {
        eligible
        paymentEligible
        eligibilityReady
      }
    }
  }
`;

const ORPHANED_PARTNERSHIP_TYPES = [
  PartnershipType.CORPORATE_BENEFITS,
  PartnershipType.INDEPENDENT_DEVELOPER,
  PartnershipType.REIT,
  PartnershipType.MARKETING,
];

// ORE, LENDER, CREDIT_AND_DEBT, AND TEST_PARTNER all get the "ORE" Experience
const ORE_PARTNERSHIP_TYPES = [
  PartnershipType.ONLINE_REAL_ESTATE,
  PartnershipType.LENDER,
  PartnershipType.CREDIT_AND_DEBT,
  PartnershipType.TEST_PARTNER,
];

// REAL_ESTATE_AGENCY get the "Agent" Experience
const AGENT_PARTNERSHIP_TYPES = [PartnershipType.REAL_ESTATE_AGENCY];

/** Fetch partnership referral data */
export const usePartnershipReferrals = (sellerInputUUID?: string): PartnershipReferralResponse => {
  const referralQuery = useQuery(
    PartnershipReferralsDocument,
    !sellerInputUUID?.trim()
      ? skipToken
      : {
          variables: { sellerInputUUID },
          fetchPolicy: 'cache-first',
          context: { linkContext: { team: 'growth' } },
        },
  );

  const referralInvitationQuery = useQuery(
    ReferralInvitationEligibilityDocument,
    !sellerInputUUID?.trim()
      ? skipToken
      : {
          variables: { sellerInputUUID },
          pollInterval: 800,
          context: { linkContext: { team: 'growth' } },
        },
  );

  const [timeoutElapsed, setTimeoutElapsed] = useState(false);
  useTimeout(() => setTimeoutElapsed(true), offerPollingTimeoutThreshold);

  const shouldStopPolling =
    referralInvitationQuery.data?.partnership.getReferralInvitationEligibility?.eligibilityReady ||
    timeoutElapsed;

  if (shouldStopPolling) {
    referralInvitationQuery.stopPolling();
  }

  const loading = referralQuery.loading || referralInvitationQuery.loading;

  const referralInvitiationEligibility =
    referralInvitationQuery.data?.partnership.getReferralInvitationEligibility?.eligible || false;
  const referralInvitation =
    referralQuery.data?.partnership.getReferralInvitation?.invitation || null;
  const referralInvitiationPaymentEligibility =
    referralInvitationQuery.data?.partnership.getReferralInvitationEligibility?.paymentEligible ||
    false;

  // Filter ORPHANED_PARTNERSHIP_TYPES out from response and set referral attributes based on response data
  const allReferrals = referralQuery.data?.partnership.getReferralsForSellerInput?.referrals || [];

  let hasHomebuilderReferral = false;
  let hasOreReferral = false;
  let hasAgentReferral = false;
  let isTrueAgentReferral = false;
  let agentReferral = undefined;
  let hasOtherReferral = false;
  let hasMarkSpainReferral = false;
  let hasEasyStreetOffersReferral = false;
  let isLennarTradeIn = false;
  const partnershipReferrals: Referral[] = [];
  const partnershipIdentifiers: string[] = [];
  const partnershipTypes: PartnershipType[] = [];
  for (const referral of allReferrals) {
    if (ORPHANED_PARTNERSHIP_TYPES.includes(referral.partnershipType)) {
      continue;
    }
    if (referral.partnershipType === PartnershipType.HOME_BUILDER) {
      hasHomebuilderReferral = true;
    }
    if (referral.partnershipType === PartnershipType.OTHERS) {
      hasOtherReferral = true;
    }
    if (ORE_PARTNERSHIP_TYPES.includes(referral.partnershipType)) {
      hasOreReferral = true;
    }
    if (AGENT_PARTNERSHIP_TYPES.includes(referral.partnershipType)) {
      hasAgentReferral = true;
      agentReferral = referral;
    }
    if (referral.partnerIdentifier === 'mark_spain_brokerage') {
      hasMarkSpainReferral = true;
    }
    if (referral.partnerIdentifier === 'easy_street_offers') {
      hasEasyStreetOffersReferral = true;
    }
    if (
      referral.partnerIdentifier === 'lennar_trade_in' &&
      referral.partnershipType === PartnershipType.HOME_BUILDER
    ) {
      isLennarTradeIn = true;
    }
    partnershipReferrals.push(referral);
    partnershipIdentifiers.push(referral.partnerIdentifier);
    partnershipTypes.push(referral.partnershipType);
  }

  if (agentReferral?.attributionType === ReferralAttributionType.REFERRAL) {
    isTrueAgentReferral = true;
  }

  const referralStep = extractPartnershipReferralStep(
    agentReferral,
    referralInvitiationEligibility,
    referralInvitation,
    referralInvitiationPaymentEligibility,
    partnershipIdentifiers,
  );

  const showBuyerContentForOreLead = BUYER_CONTENT_ELIGIBLE_ORE_PARTNER_IDENTIFIERS.some((id) =>
    partnershipIdentifiers?.includes(id),
  );

  const blockBuyerContent =
    hasAgentReferral ||
    (hasOreReferral && !showBuyerContentForOreLead) ||
    hasHomebuilderReferral ||
    hasOtherReferral;

  return {
    ...referralQuery,
    ...referralInvitationQuery,
    loading,
    isPartnershipLead: !!partnershipReferrals?.length,
    hasHomebuilderReferral,
    hasOreReferral,
    hasAgentReferral,
    partnershipIdentifiers,
    isTrueAgentReferral,
    hasMarkSpainReferral,
    hasEasyStreetOffersReferral,
    isLennarTradeIn,
    referralStep,
    blockBuyerContent,
  };
};
